import { inject, Injectable } from '@angular/core';
import { getOrderUuid, getSelectedExhibitionId } from '@app/app.reducer';
import { Store } from '@ngrx/store';
import {
  BookingPackageType,
  BookingProductType,
  BookingTariffType,
  isBookingProductTypePackage,
  isBookingProductTypeTariff,
  PostProductBooking
} from '@products/models/booking.model';
import { Package, PackageType } from '@products/models/package.model';
import { isProductTypeTariff } from '@products/models/product-selection.model';
import { ProductType, RemovePackageBooking, RemovePackageTariffBooking } from '@products/models/products.model';
import { Tariff, TariffPackageSettings, TariffType } from '@products/models/tariff.model';
import { ProductBookingService } from '@products/services/product-booking.service';
import { TariffBookingService } from '@products/services/tariff-booking.service';
import {
  getBookedPackageTypeByPackageAndTicketPersonId,
  getBookedPackageTypeByPackageNumberAndIndexes
} from '@store/products/booking/product/package/package-booking.selectors';
import { getSharedBookedPersonIdTariffsTotalCount } from '@store/products/booking/product/product-booking.selectors';
import { cloneDeep, isEmpty, isNil } from 'lodash';
import { combineLatest, first, map, Observable, of, switchMap, zip } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PackageBookingService {
  private readonly store = inject(Store);
  private readonly productBookingService = inject(ProductBookingService);
  private readonly tariffBookingService = inject(TariffBookingService);

  removePackageTariffBookings$(removePackageTariff: RemovePackageTariffBooking): Observable<{
    bookedPackageTypes: BookingPackageType[];
    removePostProductBookings: PostProductBooking[];
  }> {
    return combineLatest([
      this.store.select(getSelectedExhibitionId),
      this.store.select(getOrderUuid),
      this.store.select(
        getBookedPackageTypeByPackageAndTicketPersonId(
          removePackageTariff.packageNumber,
          removePackageTariff.packageIndex,
          removePackageTariff.ticketTypeId,
          removePackageTariff.ticketPersonId
        )
      )
    ]).pipe(
      first((combinedLatest) => combinedLatest.every((combinedItem) => !!combinedItem)),
      map(([eventId, orderUuid, bookedPackageType]) => ({ eventId, orderUuid, bookedPackageType })),
      switchMap(({ eventId, orderUuid, bookedPackageType }) =>
        zip(
          of({ eventId, orderUuid, bookedPackageType }),
          this.store.select(getSharedBookedPersonIdTariffsTotalCount([bookedPackageType]))
        ).pipe(
          map(([combinedLatest, sharedBookedPersonIdTariffsCount]) => ({
            combinedLatest,
            sharedBookedPersonIdTariffsCount
          }))
        )
      ),
      map(({ combinedLatest, sharedBookedPersonIdTariffsCount }) => {
        const { eventId, orderUuid, bookedPackageType } = combinedLatest;

        const bookingPackageTariffType = bookedPackageType.productTypes.find((packageProductType) => {
          if (isBookingProductTypeTariff(packageProductType)) {
            return packageProductType.ticketTypeId === removePackageTariff.ticketTypeId;
          }
        });

        if (isBookingProductTypeTariff(bookingPackageTariffType)) {
          const bookedTariff = bookingPackageTariffType.tariffs.find(
            (tariff) => tariff.ticketPersonId === removePackageTariff.ticketPersonId
          );

          bookedTariff.count = 0;
          bookedTariff.tariffUuids = [];
        }

        return {
          bookedPackageTypes: [bookedPackageType],
          removePostProductBookings: this.productBookingService.mapBookingProductTypeToPostProductBooking(
            [bookedPackageType],
            eventId,
            orderUuid,
            sharedBookedPersonIdTariffsCount
          )
        };
      })
    );
  }

  removePackageBookings$(removePackageBookings: RemovePackageBooking): Observable<{
    bookedPackageTypes: BookingPackageType[];
    removePostProductBookings: PostProductBooking[];
  }> {
    return combineLatest([
      this.store.select(getSelectedExhibitionId),
      this.store.select(getOrderUuid),
      this.store.select(getBookedPackageTypeByPackageNumberAndIndexes(removePackageBookings))
    ]).pipe(
      first(([eventId, orderUuid]) => !isNil(eventId) && !isEmpty(orderUuid)),
      map(([eventId, orderUuid, bookedPackageTypes]) => ({ eventId, orderUuid, bookedPackageTypes })),
      switchMap(({ eventId, orderUuid, bookedPackageTypes }) =>
        zip(
          of({ eventId, orderUuid, bookedPackageTypes }),
          this.store.select(getSharedBookedPersonIdTariffsTotalCount(bookedPackageTypes))
        ).pipe(
          map(([combinedLatest, sharedBookedPersonIdTariffsCount]) => ({
            combinedLatest,
            sharedBookedPersonIdTariffsCount
          }))
        )
      ),
      map(({ combinedLatest, sharedBookedPersonIdTariffsCount }) => {
        const { eventId, orderUuid, bookedPackageTypes } = combinedLatest;
        const emptyPackageIndexes: number[] = cloneDeep(removePackageBookings.packageIndexes);

        bookedPackageTypes.forEach((bookedPackageType) => {
          const packageIndexToRemoveFromEmptyPackageIndexes = emptyPackageIndexes.findIndex(
            (emptyPackageIndex) => emptyPackageIndex === bookedPackageType.packageIndex
          );
          emptyPackageIndexes.splice(packageIndexToRemoveFromEmptyPackageIndexes, 1);

          bookedPackageType.productTypes.forEach((packageProductType) => {
            if (isBookingProductTypeTariff(packageProductType)) {
              packageProductType.tariffs.forEach((packageTariff) => {
                packageTariff.count = 0;
                packageTariff.tariffUuids = [];
              });
            }
          });
        });

        const removePostProductBookings = this.productBookingService.mapBookingProductTypeToPostProductBooking(
          bookedPackageTypes,
          eventId,
          orderUuid,
          sharedBookedPersonIdTariffsCount
        );

        const emptyPackagesProductTypes = this.createEmptyBookingPackageTypeFromPackageNumberAndIndexes(
          removePackageBookings.packageNumber,
          emptyPackageIndexes
        );

        return {
          bookedPackageTypes: [...bookedPackageTypes, ...emptyPackagesProductTypes],
          removePostProductBookings: removePostProductBookings
        };
      })
    );
  }

  createEmptyBookingPackageTypeFromPackageNumberAndIndexes(
    packageNumber: number,
    emptyPackageIndexes: number[]
  ): BookingPackageType[] {
    const generatedEmptyBookingPackageType: BookingPackageType[] = [];

    emptyPackageIndexes.forEach((packageIndex) => {
      const emptyBookingPackageType: BookingPackageType = {
        productType: ProductType.Package,
        packageNumber: packageNumber,
        packageIndex: packageIndex,
        productTypeName: '',
        productTypes: []
      };

      generatedEmptyBookingPackageType.push(emptyBookingPackageType);
    });

    return generatedEmptyBookingPackageType;
  }

  filterPackagesFromProductBookings(bookingProductTypes: BookingProductType[]): BookingPackageType[] {
    const bookingPackageProductTypes: BookingPackageType[] = [];

    bookingProductTypes.forEach((bookingProductType) => {
      if (isBookingProductTypePackage(bookingProductType)) {
        bookingPackageProductTypes.push(bookingProductType);
      }
    });

    return bookingPackageProductTypes;
  }

  isPackageProductAddedInProductBookingList(bookingProductTypes: BookingProductType[]): boolean {
    return bookingProductTypes.some((bookingProductType) => {
      if (isBookingProductTypePackage(bookingProductType)) {
        return bookingProductType.productTypes.some((bookingPackage) => {
          if (isBookingProductTypeTariff(bookingPackage)) {
            return bookingPackage.tariffs.some((bookingPackageTariff) => !!bookingPackageTariff.count);
          }
        });
      }
    });
  }

  mapPackageTypeTariffToBookingPackageTypeTariff(
    packageType: PackageType,
    tariffType: TariffType,
    tariff: Tariff,
    previousCount: number,
    updatedCount: number
  ): BookingPackageType {
    const bookingPackageTariffType: BookingTariffType = this.tariffBookingService.mapTariffTypeToBookingTariffType(
      tariffType,
      tariff,
      previousCount,
      updatedCount
    );

    const bookingPackageType: BookingPackageType = {
      productType: ProductType.Package,
      productTypeName: packageType.info,
      packageNumber: tariff.packageNumber,
      packageIndex: tariff.packageIndex,
      productTypes: [bookingPackageTariffType]
    };

    return bookingPackageType;
  }

  mapPackageTypeToBookingPackageType(packageType: PackageType, newPackageIndex: number): BookingPackageType {
    const bookingProductTypes: BookingProductType[] = [];
    const initialPackage: Package = packageType.packages[0];

    initialPackage.products.forEach((packageProduct) => {
      if (isProductTypeTariff(packageProduct)) {
        const bookingTariffType: BookingTariffType = {
          productType: ProductType.Tariff,
          productTypeName: packageProduct.tariffType.ticketTypeName,
          ticketTypeId: packageProduct.tariffType.ticketTypeId,
          tariffs: packageProduct.tariffType.tariffs.map((packageTariff) =>
            this.tariffBookingService.mapTariffToBookingTariff(
              packageTariff,
              0,
              this.setValidatedPackageTariffAmount(packageTariff.packageSettings, 0),
              newPackageIndex
            )
          )
        };

        bookingProductTypes.push(bookingTariffType);
      }
    });

    const bookingPackageTypes: BookingPackageType = {
      productType: ProductType.Package,
      productTypeName: packageType.name,
      packageNumber: packageType.packageNumber,
      packageIndex: newPackageIndex,
      productTypes: bookingProductTypes
    };

    return bookingPackageTypes;
  }

  private setValidatedPackageTariffAmount(packageSettings: TariffPackageSettings, previousCount: number) {
    const minAmount = packageSettings.minAmount;
    const maxAmount = packageSettings.maxAmount;
    const defaultAmount = minAmount != null ? minAmount : packageSettings.amount;
    const amount = previousCount != null && previousCount ? previousCount : defaultAmount;
    const shouldSetMinTicketAmount = minAmount != null && minAmount > amount;
    const shouldSetMaxTicketAmount = maxAmount != null && maxAmount < amount;

    let ticketAmount = amount;

    if (shouldSetMaxTicketAmount) {
      ticketAmount = maxAmount;
    }

    if (shouldSetMinTicketAmount) {
      ticketAmount = minAmount;
    }

    return ticketAmount;
  }
}
